/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react'
import { Card, CardContent, Grid2 } from '@mui/material'
import i18n from 'simple-react-i18n'
import { CardTitle } from 'components/card/NewCard'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import useTitle from 'utils/customHook/useTitle'
import { PATH_PGSSE } from 'home/constants/RouteConstants'
import { useParams } from 'react-router'
import useActions from 'utils/customHook/useActions'
import { LastUpdateCard } from 'components/card/LastUpdateCard'
import ToastrAction from 'toastr/actions/ToastrAction'
import { compact } from 'lodash'
import PgsseAction from 'pgsse/action/PgsseAction'
import { push } from '@lagunovsky/redux-react-router'
import { getUdiSectorName } from 'pgsse/util/PgsseUtil'
import FormContributorPanel from './diagnostic/FormContributorPanel'
import { GroupCampaignByStatusDashboard } from './campaign/CampaignTabs'
import DocumentsPanel from './diagnostic/DocumentsPanel'
import ActivitiesPanel from './diagnostic/ActivitiesPanel'
import ExportFileModal from 'components/modal/ExportFileModal'
import { getDateExport } from 'utils/DateUtil'
import { downloadURI } from 'utils/ExportDataUtil'
import PGSSEInfos from '../common/PGSSEInfos'
import Breadcrumb from '../common/BreadCrumb'

const PgsseDiagnosticApp = () => {
    const dispatch = useDispatch()

    const { idDiagnostic } = useParams()

    const {
        distributionUnits,
        sectors,
        diagnostic: propsDiagnostic,
    } = useSelector(store => ({
        distributionUnits: store.DistributionUnitReducer.distributionUnits,
        sectors: store.SectorReducer.sectors,
        diagnostic: store.PgsseReducer.diagnostic,
    }), shallowEqual)

    const [diagnostic, setDiagnostic] = useState(propsDiagnostic)
    const [readMode, setReadMode] = useState(true)
    const [sortBy, setSortBy] = useState()
    const [isOpen, setIsOpen] = useState(false)

    useEffect(() => {
        dispatch(PgsseAction.fetchDiagnostic(idDiagnostic))
    }, [idDiagnostic])

    useEffect(() => {
        setDiagnostic(propsDiagnostic)
    }, [propsDiagnostic])

    const udi = distributionUnits.find(du => du.id === diagnostic.idUdi)
    const sector = sectors.find(s => s.id === diagnostic.idSector)
    const nameDiag = getUdiSectorName(udi, sector)

    useTitle(() => {
        return [{
            title: i18n.pgsse,
            href: PATH_PGSSE,
        }, {
            title: i18n.phase1,
            href: `${PATH_PGSSE}/phase1`,
        }, {
            title: nameDiag,
            href: `${PATH_PGSSE}/phase1/${diagnostic.idDiag}`,
        }]
    }, [diagnostic, nameDiag])

    useActions(() => {
        return readMode ? {
            edit: () => setReadMode(false),
            exportList: [{
                onClick: () => setIsOpen(true),
                label: i18n.excel,
            }],
        } : {
            cancel: () => {
                setDiagnostic(propsDiagnostic)
                setReadMode(true)
            },
            save: () => {
                dispatch(PgsseAction.updateDiagnostic(idDiagnostic, diagnostic)).then(() => setReadMode(true))
            },
        }
    }, [readMode, diagnostic, idDiagnostic])

    const campaignActions = compact([{
        icon: 'download',
        tooltip: i18n.export,
        onClick: () => {
            dispatch(ToastrAction.error(i18n.inDeveloppmentFunctionnality))
        },
    }, !readMode && {
        icon: 'note_add',
        tooltip: i18n.addCampaign,
        onClick: () => {
            dispatch(PgsseAction.createCampaign(idDiagnostic)).then(json => {
                dispatch(push(`${PATH_PGSSE}/phase1/${idDiagnostic}/campaign/${json.id}`))
            })
        },
    }])

    const onChangeDiagnostic = values => setDiagnostic(prev => ({ ...prev, ...values }))

    return (
        <Grid2 container spacing={1} sx={{ padding: '10 10 10 20' }}>
            <Grid2 size={12}>
                <Breadcrumb/>
            </Grid2>
            <Grid2 size={9}>
                <PGSSEInfos
                    diagnostic={propsDiagnostic}
                    onChangeDiagnostic={onChangeDiagnostic}
                    readMode={readMode}
                />
            </Grid2>
            <Grid2 size={3}>
                <LastUpdateCard element={diagnostic} />
            </Grid2>
            <Grid2 size={12}>
                <Card>
                    <CardTitle title={i18n.campaigns} actions={campaignActions} />
                    <CardContent sx={{ maxHeight: '300px', overflowY: 'scroll' }}>
                        <GroupCampaignByStatusDashboard
                            campaigns={diagnostic.link_campaigns}
                            sortBy={sortBy}
                            setSortBy={setSortBy}
                        />
                    </CardContent>
                </Card>
            </Grid2>
            <Grid2 size={12}>
                <FormContributorPanel
                    idDiagnostic={parseInt(idDiagnostic)}
                    data={diagnostic.link_forms}
                    readMode={readMode}
                    onChange={onChangeDiagnostic}
                />
            </Grid2>
            <Grid2 size={12}>
                <DocumentsPanel
                    link_docs={diagnostic.link_docs}
                    onChange={onChangeDiagnostic}
                    readMode={readMode}
                />
            </Grid2>
            <Grid2 size={12}>
                <ActivitiesPanel
                    link_activities={diagnostic.link_activities}
                    onChange={onChangeDiagnostic}
                    readMode={readMode}
                />
            </Grid2>
            <ExportFileModal
                open={isOpen}
                onClose={() => setIsOpen(false)}
                data={[
                    {
                        name: i18n.formExport,
                        formats: [{
                            type: i18n.excelFile,
                            callback: () => PgsseAction.promiseFetchQuestionsExport(diagnostic.idSurvey, {
                                idsDiagnostic: [diagnostic.idDiag],
                                exportType: 'xlsx',
                                fileName: `${nameDiag}_${getDateExport()}`,
                            }).then(json => {
                                if (json?.filePath) {
                                    downloadURI(json.filePath)
                                }
                            }),
                        }, {
                            type: i18n.csvFile,
                            callback: () => PgsseAction.promiseFetchQuestionsExport(diagnostic.idSurvey, {
                                idsDiagnostic: [diagnostic.idDiag],
                                exportType: 'csv',
                                fileName: `${nameDiag}_${getDateExport()}`,
                            }).then(json => {
                                downloadURI(json.filePath)
                            }),
                        }],
                    },
                    {
                        name: i18n.questionsExport,
                        formats: [{
                            type: i18n.excelFile,
                            callback: () => PgsseAction.promiseFetchQuestionsExportAll(diagnostic.idSurvey, {
                                exportType: 'xlsx',
                                fileName: `questionnaire_${getDateExport()}`,
                            }).then(json => {
                                if (json?.filePath) {
                                    downloadURI(json.filePath)
                                }
                            }),
                        }, {
                            type: i18n.csvFile,
                            callback: () => PgsseAction.promiseFetchQuestionsExportAll(diagnostic.idSurvey, {
                                exportType: 'csv',
                                fileName: `questionnaire_${getDateExport()}`,
                            }).then(json => {
                                downloadURI(json.filePath)
                            }),
                        }],
                    },
                ]}
            />
        </Grid2>
    )
}

PgsseDiagnosticApp.PropTypes = {
}

export default PgsseDiagnosticApp